import { t } from '../../utils/i18n';
import { form, selectedDeliveryMode, setSelectedDeliveryMode, scopeProceduresToSelectedLocation } from '../../utils/variables';
import { procedureDeliveryBucket } from '../../utils/deliveryMode';
import { handleLocationPage } from './locationListeners';

/**
 * "How would you like to meet?" step. Shown between the location and service
 * steps only when the appointment channel has `delivery_mode_selection` on.
 * The chosen mode ('virtual' | 'phone' | 'in_person') is stored in
 * `selectedDeliveryMode` and later used by goToServices() to filter the
 * service list by `service_delivery_mode`.
 *
 * Options are derived from the channel's actual services at the selected
 * location — a bucket with no bookable services renders no option (a practice
 * that only offers phone consultations never shows "Virtual"). When exactly
 * one bucket remains, it is auto-selected and the step is skipped entirely:
 * this function returns false and the caller must NOT navigate to the step.
 */
export const deliveryModeListeners = () => {
    const buckets = bucketedProcedures();
    const availableModes = Object.keys(buckets).filter((mode) => buckets[mode].length > 0);

    if (availableModes.length === 1) {
        setSelectedDeliveryMode(availableModes[0]);
        return false;
    }

    // Localize static labels.
    const title = document.querySelector('.deliveryMode__container .deliveryMode--title');
    if (title) title.textContent = t('select_visit_type');

    document
        .querySelectorAll('.deliveryMode__container .deliveryMode__option')
        .forEach((option) => {
            const mode = option.getAttribute('data-delivery-mode');

            // Misconfigured channels (every bucket empty) keep the legacy
            // two-option step rather than rendering nothing.
            const hide = availableModes.length > 0
                ? !availableModes.includes(mode)
                : mode === 'phone';
            option.classList.toggle('hidden', hide);

            const titleEl = option.querySelector('.deliveryMode__option-title');
            const descEl = option.querySelector('.deliveryMode__option-desc');
            if (titleEl) titleEl.textContent = optionTitle(mode);
            if (descEl) descEl.textContent = optionDesc(mode, buckets[mode] ?? []);

            option.onclick = () => selectDeliveryMode(mode);
        });

    // Re-highlight a prior selection, otherwise start clean.
    if (selectedDeliveryMode) {
        selectDeliveryMode(selectedDeliveryMode);
    } else {
        removeDeliveryModeSelection();
    }

    document
        .querySelector('.deliveryModeContinue__btn')
        .onclick = deliveryModeContinueHandler;

    return true;
};

function bucketedProcedures() {
    const list = scopeProceduresToSelectedLocation(
        (form?.appointment_channel?.procedures ?? []).filter((p) => p?.online_booking)
    ).filter((p) => p?.online_booking_option !== 'inquiry');

    const buckets = { in_person: [], virtual: [], phone: [] };
    list.forEach((p) => buckets[procedureDeliveryBucket(p)].push(p));
    return buckets;
}

function optionTitle(mode) {
    if (mode === 'virtual') return t('virtual');
    if (mode === 'phone') return t('phone_call');
    return t('in_person');
}

function optionDesc(mode, procedures) {
    if (mode === 'virtual') return t('virtual_visit_desc');
    if (mode === 'phone') {
        // "Meet for a 20-minute phone call" when every phone service shares
        // one duration; generic copy otherwise.
        const durations = [...new Set(procedures.map((p) => parseInt(p?.duration, 10) || 0).filter(Boolean))];
        return durations.length === 1
            ? t('phone_visit_desc_duration').replace('{duration}', durations[0])
            : t('phone_visit_desc');
    }
    return t('in_person_visit_desc');
}

function deliveryModeContinueHandler() {
    if (!selectedDeliveryMode) return;
    handleLocationPage();
}

function selectDeliveryMode(mode) {
    removeDeliveryModeSelection();
    setSelectedDeliveryMode(mode);

    document
        .querySelector(`.deliveryMode__option[data-delivery-mode="${CSS.escape(mode)}"]`)
        ?.classList.add('is-selected');

    document
        .querySelector('.deliveryModeContinue__btn')
        .classList.remove('disabled');
}

function removeDeliveryModeSelection() {
    document
        .querySelectorAll('.deliveryMode__container .deliveryMode__option')
        .forEach((i) => i.classList.remove('is-selected'));

    document.querySelector('.deliveryModeContinue__btn').classList.add('disabled');
}
